import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { PermissionsProvider } from './context/PermissionsContext';
// Web3Provider removed - wallet connection feature disabled in favor of manual TX hash deposits
import { ThemeProvider } from './context/ThemeContext';
import { LanguageProvider } from './context/LanguageContext';
import { NotificationProvider, useNotification } from './context/NotificationContext';
import Layout from './components/layout/Layout';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import TelegramCallbackPage from './pages/TelegramCallbackPage';
import GoogleAuthCallback from './pages/GoogleAuthCallback';
import HomePage from './pages/HomePage';
import TradingPage from './pages/TradingPage';
import CoinDetailPage from './pages/CoinDetailPage';
import ProfilePage from './pages/ProfilePage';
import WalletPage from './pages/WalletPage';
import IDOPage from './pages/IDOPage';
import SecurityPage from './pages/SecurityPage';
import SupportPage from './pages/SupportPage';
import NotificationsPage from './pages/NotificationsPage';
// Admin Panel
import AdminLayout from './pages/admin/AdminLayout';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminClients from './pages/admin/AdminClients';
import AdminPositions from './pages/admin/AdminPositions';
import AdminSupport from './pages/admin/AdminSupport';
import AdminTeam from './pages/admin/AdminTeam';
import AdminSettings from './pages/admin/AdminSettings';
import AdminRequests from './pages/admin/AdminRequests';
import AdminAuditLog from './pages/admin/AdminAuditLog';
import AdminIDO from './pages/admin/AdminIDO';
import AdminEmailSettings from './pages/admin/AdminEmailSettings';
// Support role pages
import SupportDashboard from './pages/admin/SupportDashboard';
import SupportTickets from './pages/admin/SupportTickets';
import './App.css';

// WebSocket Connection Manager - connects when user logs in
const WebSocketManager = ({ children }) => {
  const { user, token, isAuthenticated } = useAuth();
  const { connectWebSocket, disconnectWebSocket } = useNotification();

  useEffect(() => {
    if (isAuthenticated && user?.id && token) {
      // Small delay to ensure context is ready
      const timer = setTimeout(() => {
        connectWebSocket(user.id, token);
      }, 500);
      return () => clearTimeout(timer);
    } else {
      disconnectWebSocket();
    }

    return () => {
      disconnectWebSocket();
    };
  }, [isAuthenticated, user?.id, token, connectWebSocket, disconnectWebSocket]);

  return children;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  // If user data was passed from OAuth callback, skip loading check
  if (location.state?.user) {
    return children;
  }

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: '#0B0E11',
        color: '#EAECEF'
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

// Public Route Component (redirect to appropriate page if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: '#0B0E11',
        color: '#EAECEF'
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (isAuthenticated) {
    // Redirect based on role
    const staffRoles = ['super_admin', 'admin', 'manager'];
    if (user?.role === 'support') {
      return <Navigate to="/admin-panel/support-dashboard" replace />;
    } else if (staffRoles.includes(user?.role)) {
      return <Navigate to="/admin-panel" replace />;
    }
    return <Navigate to="/" replace />;
  }

  return children;
};

// App Router with OAuth detection
// CRITICAL: Detect session_id during render, NOT in useEffect (prevents race conditions)
const AppRouter = () => {
  const location = useLocation();
  
  // Check URL hash for Google OAuth session_id (synchronous check)
  if (location.hash?.includes('session_id=')) {
    return <GoogleAuthCallback />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        }
      />
      
      {/* Password Reset */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      
      {/* OAuth Callbacks */}
      <Route path="/telegram-callback" element={<TelegramCallbackPage />} />
      <Route path="/auth/callback" element={<GoogleAuthCallback />} />

      {/* Protected Routes - Main App */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        <Route index element={<HomePage />} />
        <Route path="trading" element={<TradingPage />} />
        <Route path="coin/:coinId" element={<CoinDetailPage />} />
        <Route path="ido" element={<IDOPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="wallet" element={<WalletPage />} />
        {/* Redirect old routes to /wallet */}
        <Route path="deposits" element={<Navigate to="/wallet" replace />} />
        <Route path="withdrawals" element={<Navigate to="/wallet" replace />} />
        {/* Redirect old /admin to new admin panel */}
        <Route path="admin" element={<Navigate to="/admin-panel" replace />} />
        <Route path="security" element={<SecurityPage />} />
        <Route path="support" element={<SupportPage />} />
        <Route path="notifications" element={<NotificationsPage />} />
      </Route>

      {/* Admin Panel Routes */}
      <Route
        path="/admin-panel"
        element={
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="clients" element={<AdminClients />} />
        <Route path="positions" element={<AdminPositions />} />
        <Route path="requests" element={<AdminRequests />} />
        <Route path="support" element={<AdminSupport />} />
        <Route path="team" element={<AdminTeam />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="audit-log" element={<AdminAuditLog />} />
        <Route path="ido" element={<AdminIDO />} />
        <Route path="email" element={<AdminEmailSettings />} />
        {/* Support role specific routes */}
        <Route path="support-dashboard" element={<SupportDashboard />} />
        <Route path="new-clients" element={<SupportDashboard />} />
        <Route path="support-tickets" element={<SupportTickets />} />
      </Route>

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <NotificationProvider>
              <WebSocketManager>
                <PermissionsProvider>
                  <AppRouter />
                </PermissionsProvider>
              </WebSocketManager>
            </NotificationProvider>
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}

export default App;
